/** @module store/amperixes */
import * as apiAmperixes from '@/api/amperixes'

const state = () => ({
  emsToAmperix: {}
})

const mutations = {
  SET_AMPERIX: (state, { amperix }) => {
    if (!(amperix instanceof Amperix) || !amperix?.energyManagementSystemId) return

    state.emsToAmperix[amperix.energyManagementSystemId] = amperix
  },
  SET_AMPERIXES: (state, { amperixes }) => {
    state.emsToAmperix = amperixes
      .filter((apx) => apx instanceof Amperix && apx?.energyManagementSystemId)
      .reduce((prev, apx) => ({ ...prev, [apx.energyManagementSystemId]: apx }), {})
  }
}

const getters = {
  getAmperixBySerialNumber: (state) => (serialNumber) =>
    Object.values(state.emsToAmperix).find((apx) => apx.serialNumber === serialNumber),
  getAmperix: (state) => (emsId) => state.emsToAmperix[emsId],
  hwVersions: (state) =>
    [...new Set(Object.values(state.emsToAmperix).map((apx) => apx.hwVersion))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    ),
  licenseTypes: (state) =>
    [...new Set(Object.values(state.emsToAmperix).map((apx) => apx.licenseType))]
      .filter((licenseType) => licenseType)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
  swVersions: (state) =>
    [...new Set(Object.values(state.emsToAmperix).map((apx) => apx.swVersion))]
      .filter((swVersion) => swVersion)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
  swVersionsInitial: (state) =>
    [...new Set(Object.values(state.emsToAmperix).map((apx) => apx.swVersionInitial))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    )
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions: {
    getAmperix: ({ commit }, { ctxt, serialNumber }) =>
      apiAmperixes.getAmperix({ ctxt, serialNumber }).then((msg) => {
        commit('SET_AMPERIX', {
          amperix: resourceToAmperix(msg?.payload?.data, msg?.payload?.included)
        })
      }),
    getAmperixes: ({ commit }, { ctxt }) =>
      apiAmperixes.getAmperixes({ ctxt }).then((msg) => {
        commit('SET_AMPERIXES', {
          amperixes: msg?.payload?.data?.map((apxResource) => resourceToAmperix(apxResource, msg?.payload?.included))
        })
      })
  }
}

export class Amperix {
  hwSerialNumber
  hwVersion
  licenseType
  swVersion
  productionDate
  serialNumber

  constructor({
    energyManagementSystemId,
    hwSerialno,
    hwVersion,
    licenseType,
    mac,
    swVersion,
    productiondate,
    serialno,
    swVersionInitial
  }) {
    if (!energyManagementSystemId) throw new TypeError('An EMS-ID has to be present')
    this.energyManagementSystemId = parseInt(energyManagementSystemId)
    this.hwSerialNumber = hwSerialno
    this.hwVersion = hwVersion
    this.licenseType = licenseType
    this._macAddress = mac
    this.swVersion = swVersion
    this.productionDate = moment(productiondate)
    this.serialNumber = serialno
    this._swVersionInitial = swVersionInitial
  }

  // Note: different meta-data schema-versions may have different values for the
  // same field. Because we do NOT track the schema-version, we just ducktype.

  get macAddress() {
    if (Array.isArray(this._macAddress)) {
      const macEntry = this._macAddress.find((mac) => {
        const iname = mac.interfaceName
        if (!iname) {
          return false
        }
        return /^(eth0|int)$/i.test(iname)
      })
      return macEntry?.macAddress
    } else if (typeof this._macAddress == 'string') {
      return this._macAddress
    } else {
      return undefined
    }
  }

  get swVersionInitial() {
    if (Array.isArray(this._swVersionInitial)) {
      const swEntry = this._swVersionInitial.find((sw) => {
        const part = sw.part
        if (!part) {
          return false
        }

        return /^hems.*/i.test(part)
      })
      return swEntry?.version
    } else if (typeof this._swVersionInitial == 'string') {
      return this._swVersionInitial
    } else {
      return undefined
    }
  }
}

export const resourceToAmperix = (apxResource, includedResources) => {
  if (!apxResource.attributes?.meta) return
  if (!apxResource?.relationships?.energyManagementSystem?.data?.id) return

  const amperixLicenseResource = includedResources?.find(
    (item) => item.type === 'amperix_licenses' && item.id === apxResource.relationships?.amperixLicense?.data?.id
  )

  const metaInfo = Object.entries(apxResource.attributes.meta.amperix).reduce(
    (prev, [key, value]) => ({ [_.camelCase(key)]: value, ...prev }),
    {}
  )

  const params = {
    swVersion: apxResource?.attributes?.version,
    licenseType: amperixLicenseResource?.attributes?.licenseType,
    energyManagementSystemId: apxResource?.relationships?.energyManagementSystem?.data?.id,
    ...metaInfo
  }

  return new Amperix(params)
}
